import { useEffect, useRef, useState } from 'react';
import { ExtendedInput } from '@geneui/components';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { isFunction, isNil } from 'lodash';

const TemplateTextInput = ({
    displayNameKey,
    value,
    onBlur,
    onFocus,
    onChange,
    validationState,
    updateInsertionHandler,
}) => {
    const { t } = useTranslation();

    const inputRef = useRef<HTMLInputElement>(null);
    const [currentValue, setCurrentValue] = useState(value);
    const [moveCursorToPosition, setMoveCursorToPosition] = useState<number | null>(null);

    const insertTextAtCursor = (dynamicVariable) => {
        const textToInsert = dynamicVariable.FieldName;
        inputRef.current?.focus();
        const selectionStart = inputRef.current?.selectionStart;
        const firstPart = currentValue.slice(0, selectionStart);
        const secondPart = currentValue.slice(selectionStart, currentValue.length);
        const insertedText = ` {${textToInsert}} `;
        const resultValue = `${firstPart}${insertedText}${secondPart}`;

        setCurrentValue(resultValue);
        setMoveCursorToPosition(firstPart.length + insertedText.length);
        onChange(resultValue, 0);
    };

    if (isFunction(updateInsertionHandler)) updateInsertionHandler(insertTextAtCursor);

    useEffect(() => {
        setCurrentValue(value);
    }, [value]);

    useEffect(() => {
        onFocus();
    }, [onFocus]);

    // The caret is placed after the input receives the new value; setting it earlier is lost
    // because writing the controlled value moves the caret to the end.
    useEffect(() => {
        if (!isNil(moveCursorToPosition)) {
            inputRef.current?.focus();
            inputRef.current?.setSelectionRange(moveCursorToPosition, moveCursorToPosition);
            setMoveCursorToPosition(null);
        }
    }, [moveCursorToPosition]);

    const handleChange = (e) => {
        const value = e.target.value;
        if (value === ' ') return;
        setCurrentValue(value);
        onChange(value);
    };

    return (
        <div className="crm-create-template-page-c-c-h-inputs-column">
            <ExtendedInput
                ref={inputRef}
                placeholder={t(displayNameKey)}
                value={currentValue}
                isValid={validationState.isValid}
                errorText={validationState.errorText}
                onBlur={(e) => onBlur?.(e.target.value.trim(), 0)}
                onChange={handleChange}
                onFocus={onFocus}
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus={true}
                labelAppearance="swap"
            />
        </div>
    );
};

TemplateTextInput.propTypes = {
    displayNameKey: PropTypes.string.isRequired,
    value: PropTypes.string,
    onBlur: PropTypes.func,
    onFocus: PropTypes.func,
    onChange: PropTypes.func.isRequired,
    validationState: PropTypes.shape({
        isValid: PropTypes.bool,
        errorText: PropTypes.string,
    }),
    updateInsertionHandler: PropTypes.func,
};

export default TemplateTextInput;
